import React, { useEffect, useMemo, useState } from "react";

function stars(rating) {
  const count = Math.max(1, Math.min(5, Number(rating || 0)));
  return "★".repeat(count) + "☆".repeat(5 - count);
}

function Testimonials({ testimonials = [] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === "undefined" ? 1440 : window.innerWidth));

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const visibleCount = useMemo(() => {
    if (viewportWidth <= 640) return 1;
    return 2;
  }, [viewportWidth]);

  const maxIndex = Math.max(0, testimonials.length - visibleCount);

  useEffect(() => {
    setActiveIndex((current) => Math.min(current, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    if (paused || testimonials.length <= visibleCount) return undefined;
    const timer = setInterval(() => {
      setActiveIndex((current) => (current >= maxIndex ? 0 : current + 1));
    }, 7600);
    return () => clearInterval(timer);
  }, [maxIndex, paused, testimonials.length, visibleCount]);

  if (!testimonials.length) return null;

  return (
    <section style={{ padding: "64px 16px", background: "linear-gradient(180deg, #ffffff 0%, #fff7ed 100%)", overflow: "hidden" }}>
      <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
        <div style={{ marginBottom: "28px", textAlign: "center" }}>
          <p style={{ margin: 0, color: "#7c2d3a", fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase", fontSize: "12px" }}>
            Student Testimonials
          </p>
          <h2 style={{ margin: "10px 0 8px", color: "#0f172a", fontSize: "clamp(26px, 3vw, 38px)", fontWeight: 900 }}>
            What Our Students Say
          </h2>
          <p style={{ margin: "0 auto", color: "#64748b", maxWidth: "680px", lineHeight: 1.7 }}>
            Approved student feedback is published here directly from the admin review panel.
          </p>
        </div>

        <div
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          style={{ position: "relative" }}
        >
          <div style={{ overflow: "hidden", margin: "0 auto" }}>
            <div
              style={{
                display: "flex",
                transition: "transform 1100ms cubic-bezier(0.22, 1, 0.36, 1)",
                transform: `translateX(-${activeIndex * (100 / visibleCount)}%)`,
              }}
            >
              {testimonials.map((item) => (
                <div key={item.id} style={{ flex: `0 0 ${100 / visibleCount}%`, padding: "8px 10px", boxSizing: "border-box" }}>
                  <article
                    style={{
                      minHeight: "250px",
                      height: "100%",
                      background: "linear-gradient(180deg, #ffffff 0%, #fffaf5 100%)",
                      border: "1px solid rgba(148, 163, 184, 0.18)",
                      borderRadius: "20px",
                      padding: "18px",
                      boxShadow: "0 20px 38px rgba(15, 23, 42, 0.1)",
                      display: "grid",
                      gridTemplateRows: "auto auto auto auto 1fr",
                      alignItems: "start",
                      gap: "8px",
                      position: "relative",
                      overflow: "hidden",
                      textAlign: "center",
                    }}
                  >
                    <span style={{ position: "absolute", right: "18px", top: "10px", color: "rgba(124,45,58,0.08)", fontSize: "72px", lineHeight: 1, fontWeight: 900 }}>“</span>
                    <div style={{ display: "flex", justifyContent: "center", position: "relative" }}>
                      {item.student_photo_url ? (
                        <img
                          src={item.student_photo_url}
                          alt={item.student_name || "Student"}
                          style={{ width: "62px", height: "62px", borderRadius: "18px", objectFit: "cover", border: "3px solid #fff", boxShadow: "0 12px 20px rgba(124,45,58,0.16)" }}
                        />
                      ) : (
                        <span style={{ width: "62px", height: "62px", borderRadius: "18px", background: "#fff7ed", color: "#7c2d3a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: "24px", boxShadow: "0 12px 20px rgba(124,45,58,0.12)" }}>
                          {(item.student_name || "S").slice(0, 1).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <h3 style={{ margin: "2px 0 0", color: "#0f172a", fontSize: "17px", fontWeight: 900, position: "relative" }}>{item.student_name || "Student"}</h3>
                    <p style={{ margin: 0, color: "#64748b", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", position: "relative" }}>
                      {item.centre_name || item.registration_no || "NIDCS Student"}
                    </p>
                    <div style={{ color: "#f59e0b", fontWeight: 900, letterSpacing: "1px", fontSize: "17px", position: "relative" }}>{stars(item.rating)}</div>
                    <p style={{ margin: 0, color: "#334155", lineHeight: 1.65, position: "relative" }}>{item.feedback}</p>
                  </article>
                </div>
              ))}
            </div>
          </div>

          {testimonials.length > visibleCount ? (
            <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "18px" }}>
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Show testimonial ${index + 1}`}
                  style={{
                    width: activeIndex === index ? "28px" : "10px",
                    height: "10px",
                    borderRadius: "999px",
                    border: "none",
                    background: activeIndex === index ? "#7c2d3a" : "rgba(124,45,58,0.22)",
                    transition: "all 220ms ease",
                  }}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default Testimonials;
